<template>
  <div :class="$style.payoutsSection">
    <BaseCard fullWidth>
      <Flex direction="column">
        <BaseHeading size="s">
          {{ $t('register.salonized_pay.dashboard.next_payout') }}
        </BaseHeading>
        <BaseText
          size="l"
          bold
          v-test="'sp-payouts-next-payout'"
        >
          {{ filters.currency(availableBalance) }}
        </BaseText>
      </Flex>
    </BaseCard>
    <BaseCard fullWidth>
      <div :class="$style.payout">
        <Flex direction="column">
          <BaseHeading size="s">
            {{ $t('register.salonized_pay.dashboard.processing') }}
          </BaseHeading>
          <BaseText
            size="l"
            bold
            v-test="'sp-payouts-processing'"
          >
            {{ filters.currency(processingBalance) }}
          </BaseText>
        </Flex>
        <BaseText
          @click="$emit('showPayoutSchedule')"
          v-test="'sp-payouts-schedule-link'"
        >
          {{ $t('register.salonized_pay.dashboard.schedule.link') }}
        </BaseText>
      </div>
    </BaseCard>
  </div>
  <BaseCard
    :heading="$t('register.salonized_pay.dashboard.payout_history.heading')"
  >
    <template
      v-if="showWalletFilter"
      #header
    >
      <div :class="$style.walletFilter">
        <BaseDropdown
          v-model="balanceAccountId"
          :options="walletOptions"
          v-test="'sp-payout-history-wallet-filter'"
        />
      </div>
    </template>
    <PayoutHistoryTable
      :balanceAccountId="balanceAccountId"
      :multipleWallets="showWalletFilter"
    />
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import type { AdyenBalanceAccount } from '@/types';
import unleash from '@/unleash';
import { GET_ADYEN_WALLETS } from '../graphql';
import PayoutHistoryTable from './PayoutHistoryTable.vue';
import { walletName } from './wallet-share';

const props = defineProps<{
  availableBalance: number;
  processingBalance: number;
  initialBalanceAccountId?: number | null;
}>();

defineEmits(['showPayoutSchedule']);

const { t } = useI18n();

const splitWalletsEnabled = unleash.isEnabled('SplitWallets');

const { result } = useQuery(GET_ADYEN_WALLETS, null, {
  enabled: splitWalletsEnabled,
});

const balanceAccounts = computed<AdyenBalanceAccount[]>(
  () => result.value?.adyenAppState?.balanceAccounts ?? [],
);

const showWalletFilter = computed(() => balanceAccounts.value.length > 1);

// null shows the payouts of every wallet.
const balanceAccountId = ref<number | null>(
  props.initialBalanceAccountId ?? null,
);

const walletOptions = computed(() => [
  {
    value: null,
    label: t('register.salonized_pay.dashboard.payout_history.all_wallets'),
  },
  ...balanceAccounts.value.map((balanceAccount) => ({
    value: balanceAccount.id,
    label: walletName(balanceAccount),
  })),
]);
</script>

<style
  lang="scss"
  module
>
.payoutsSection {
  display: flex;
  gap: $spacing;

  & > * {
    width: 50%;
  }
}

.walletFilter {
  width: 200px;
}

.payout {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}
</style>
